const TEMPLATE_SECTION_ORDER_DEFAULT = [
  "medications",
  "chiefComplaints",
  "historyOfPresentIllness",
  "historyOfPastIllness",
  "vitals",
  "diagnosis",
  "testsInvestigations",
  "medicalAdvice",
  "followUp",
  "doctorSignature",
  "referral",
  "drugHistory",
  "adultVaccination",
  "dentalExamination",
];

const TEMPLATE_BOOLEAN_DEFAULTS = {
  showFooter: true,
  showTopHeader: true,
  showDoctorSignature: true,
  useBackgroundTemplate: false,
};

const TEMPLATE_NUMBER_DEFAULTS = {
  printOfsetTop: 0,
  printOfsetLeft: 0,
  extraRightMargin: 0,
  extraBottomMargin: 0,
  extraLeftMargin: 0,
  extraTopMargin: 0,
};

const parseJsonSafely = (value) => {
  if (value == null) return null;
  if (typeof value === "string") {
    try {
      return JSON.parse(value);
    } catch (error) {
      return null;
    }
  }
  return typeof value === "object" ? value : null;
};

const hasVisibilityBooleans = (visibility) =>
  visibility != null &&
  typeof visibility === "object" &&
  !Array.isArray(visibility) &&
  Object.values(visibility).some((v) => typeof v === "boolean");

const normalizeSectionOrder = (rawOrder) => {
  if (!Array.isArray(rawOrder)) return [...TEMPLATE_SECTION_ORDER_DEFAULT];
  const cleaned = rawOrder
    .map((entry) => (typeof entry === "string" ? entry.trim() : ""))
    .filter(Boolean);
  if (!cleaned.length) return [...TEMPLATE_SECTION_ORDER_DEFAULT];
  return Array.from(new Set(cleaned));
};

const normalizeSectionsVisibility = (rawVisibility, sectionOrder) => {
  const output = {};
  const source =
    rawVisibility && typeof rawVisibility === "object" && !Array.isArray(rawVisibility)
      ? rawVisibility
      : {};
  sectionOrder.forEach((sectionId) => {
    const value = source[sectionId];
    output[sectionId] = typeof value === "boolean" ? value : true;
  });
  return output;
};

const toBoolean = (value, fallback) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const lowered = value.toLowerCase();
    if (lowered === "true") return true;
    if (lowered === "false") return false;
  }
  return fallback;
};

const toNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const mergeScalarTemplateFields = (doctorConfig, brandingConfig) => {
  const merged = { ...brandingConfig, ...doctorConfig };
  return {
    showFooter: toBoolean(merged.showFooter, TEMPLATE_BOOLEAN_DEFAULTS.showFooter),
    showTopHeader: toBoolean(
      merged.showTopHeader,
      TEMPLATE_BOOLEAN_DEFAULTS.showTopHeader,
    ),
    showDoctorSignature: toBoolean(
      merged.showDoctorSignature,
      TEMPLATE_BOOLEAN_DEFAULTS.showDoctorSignature,
    ),
    useBackgroundTemplate: toBoolean(
      merged.useBackgroundTemplate,
      TEMPLATE_BOOLEAN_DEFAULTS.useBackgroundTemplate,
    ),
    printOfsetTop: toNumber(merged.printOfsetTop, TEMPLATE_NUMBER_DEFAULTS.printOfsetTop),
    printOfsetLeft: toNumber(
      merged.printOfsetLeft,
      TEMPLATE_NUMBER_DEFAULTS.printOfsetLeft,
    ),
    extraRightMargin: toNumber(
      merged.extraRightMargin,
      TEMPLATE_NUMBER_DEFAULTS.extraRightMargin,
    ),
    extraBottomMargin: toNumber(
      merged.extraBottomMargin,
      TEMPLATE_NUMBER_DEFAULTS.extraBottomMargin,
    ),
    extraLeftMargin: toNumber(
      merged.extraLeftMargin,
      TEMPLATE_NUMBER_DEFAULTS.extraLeftMargin,
    ),
    extraTopMargin: toNumber(
      merged.extraTopMargin,
      TEMPLATE_NUMBER_DEFAULTS.extraTopMargin,
    ),
  };
};

export const parseTemplateConfigInput = (value) => parseJsonSafely(value);

export const getBrandingTemplateConfig = (brandingData) => {
  if (!Array.isArray(brandingData)) return {};
  const templateConfigItem = brandingData.find(
    (item) =>
      item?.name === "template_config" || item?.name === "prescription_config",
  );
  return parseJsonSafely(templateConfigItem?.content) || {};
};

/** Normalize template_config for the doctor edit modal (doctor values override branding). */
export const getTemplateConfigForEditForm = (
  templateConfigInput,
  brandingTemplateConfig = {},
) => {
  const doctorConfig = parseJsonSafely(templateConfigInput) || {};
  const brandingConfig = parseJsonSafely(brandingTemplateConfig) || {};

  const resolvedSectionOrder =
    Array.isArray(doctorConfig.sectionOrder) && doctorConfig.sectionOrder.length > 0
      ? doctorConfig.sectionOrder
      : Array.isArray(brandingConfig.sectionOrder) &&
          brandingConfig.sectionOrder.length > 0
        ? brandingConfig.sectionOrder
        : null;

  const sectionOrder = normalizeSectionOrder(resolvedSectionOrder);

  const resolvedVisibility = hasVisibilityBooleans(doctorConfig.sectionsVisibility)
    ? doctorConfig.sectionsVisibility
    : hasVisibilityBooleans(brandingConfig.sectionsVisibility)
      ? brandingConfig.sectionsVisibility
      : null;

  const sectionsVisibility = normalizeSectionsVisibility(
    resolvedVisibility,
    sectionOrder,
  );

  return {
    ...brandingConfig,
    ...doctorConfig,
    ...mergeScalarTemplateFields(doctorConfig, brandingConfig),
    sectionOrder,
    sectionsVisibility,
  };
};

/**
 * Build API payload. Section order/visibility are included only when the doctor
 * record already has them — avoids injecting defaults on unrelated inline edits.
 */
export const buildTemplateConfigPayload = (
  templateConfigInput,
  brandingTemplateConfig = {},
) => {
  const doctorConfig = parseJsonSafely(templateConfigInput) || {};
  const brandingConfig = parseJsonSafely(brandingTemplateConfig) || {};

  const payload = mergeScalarTemplateFields(doctorConfig, brandingConfig);

  const hasExplicitSectionOrder =
    Array.isArray(doctorConfig.sectionOrder) && doctorConfig.sectionOrder.length > 0;
  const hasExplicitSectionsVisibility = hasVisibilityBooleans(
    doctorConfig.sectionsVisibility,
  );

  if (hasExplicitSectionOrder || hasExplicitSectionsVisibility) {
    const normalized = getTemplateConfigForEditForm(
      templateConfigInput,
      brandingTemplateConfig,
    );
    if (hasExplicitSectionOrder) {
      payload.sectionOrder = normalized.sectionOrder;
    }
    if (hasExplicitSectionsVisibility) {
      payload.sectionsVisibility = normalized.sectionsVisibility;
    }
  }

  return payload;
};
